import type {
  ApprovalView,
  AuthMeResponse,
  ClaudeMdResponse,
  ClaudeMdRevision,
  CreateTurnResponse,
  EmployeeConfigView,
  EmployeeRunnerView,
  EmployeeUsageResponse,
  FileContentResponse,
  LoginResponse,
  McpServerConfig,
  McpServerView,
  MemoryIndexResponse,
  MemoryTreeResponse,
  MessageView,
  PatchEmployeeRequest,
  ResolveApprovalResponse,
  RunnerState,
  SpaceHistoryItem,
  SpaceListItem,
  ThreadDetailResponse,
  ThreadListResponse,
  ThreadView,
  ToolCallView,
  ToolGrantView,
} from '@omni/shared';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiRequest, downloadUrl } from './client';

/**
 * Typed TanStack Query surface over CONTRACTS §4. Every response type is the
 * frozen DTO from `@omni/shared`, imported **type-only** (SPEC §4) so no runtime
 * byte of the contracts package reaches the browser bundle.
 */

export const qk = {
  me: ['me'] as const,
  employee: ['employee'] as const,
  mcp: ['employee', 'mcp'] as const,
  toolGrants: ['employee', 'tool-grants'] as const,
  runner: ['employee', 'runner'] as const,
  usage: (from?: string, to?: string) => ['employee', 'usage', from ?? '', to ?? ''] as const,
  threads: (archived: boolean) => ['threads', archived] as const,
  thread: (id: string) => ['thread', id] as const,
  approvals: ['approvals', 'pending'] as const,
  memoryTree: (path: string) => ['memory', 'tree', path] as const,
  memoryFile: (path: string) => ['memory', 'file', path] as const,
  memoryIndex: ['memory', 'index'] as const,
  claudeMd: ['memory', 'claude-md'] as const,
  claudeMdRevisions: ['memory', 'claude-md', 'revisions'] as const,
  taskLog: (tail: number) => ['memory', 'task-log', tail] as const,
  spaces: ['spaces'] as const,
  spaceTree: (slug: string, path: string) => ['spaces', slug, 'tree', path] as const,
  spaceFile: (slug: string, path: string) => ['spaces', slug, 'file', path] as const,
  spaceHistory: (slug: string, path: string) => ['spaces', slug, 'history', path] as const,
  spaceBlob: (slug: string, sha: string, path: string) =>
    ['spaces', slug, 'blob', sha, path] as const,
};

/* ── auth (§4.1) ──────────────────────────────────────────────────────────── */

export function useMe(enabled = true): UseQueryResult<AuthMeResponse, Error> {
  return useQuery({
    queryKey: qk.me,
    queryFn: () => apiRequest<AuthMeResponse>('/auth/me'),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}

export function useLogin(): UseMutationResult<
  LoginResponse,
  Error,
  { email: string; password: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiRequest<LoginResponse>('/auth/login', { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries();
    },
  });
}

export function useLogout(): UseMutationResult<void, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest<void>('/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      qc.clear();
    },
  });
}

export function useChangePassword(): UseMutationResult<
  void,
  Error,
  { currentPassword: string; newPassword: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiRequest<void>('/auth/password', { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}

/* ── employee & settings (§4.2) ───────────────────────────────────────────── */

export function useEmployee(): UseQueryResult<EmployeeConfigView, Error> {
  return useQuery({
    queryKey: qk.employee,
    queryFn: () => apiRequest<EmployeeConfigView>('/employee'),
  });
}

export function usePatchEmployee(): UseMutationResult<
  EmployeeConfigView,
  Error,
  PatchEmployeeRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiRequest<EmployeeConfigView>('/employee', { method: 'PATCH', body }),
    onSuccess: (data) => {
      qc.setQueryData(qk.employee, data);
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}

export function useMcpServers(): UseQueryResult<Record<string, McpServerView>, Error> {
  return useQuery({
    queryKey: qk.mcp,
    queryFn: async () => {
      const res = await apiRequest<{ servers: Record<string, McpServerView> }>('/employee/mcp');
      return res.servers;
    },
  });
}

export function usePutMcpServers(): UseMutationResult<
  { servers: Record<string, McpServerView> },
  Error,
  Record<string, McpServerConfig>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (servers) =>
      apiRequest<{ servers: Record<string, McpServerView> }>('/employee/mcp', {
        method: 'PUT',
        body: { servers },
      }),
    onSuccess: (data) => {
      qc.setQueryData(qk.mcp, data.servers);
    },
  });
}

export function useToolGrants(): UseQueryResult<ToolGrantView[], Error> {
  return useQuery({
    queryKey: qk.toolGrants,
    queryFn: async () => {
      const res = await apiRequest<{ items: ToolGrantView[] }>('/employee/tool-grants');
      return res.items;
    },
  });
}

export function useRevokeToolGrant(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (toolName) =>
      apiRequest<void>(`/employee/tool-grants/${encodeURIComponent(toolName)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.toolGrants });
    },
  });
}

export function useRunner(): UseQueryResult<EmployeeRunnerView, Error> {
  return useQuery({
    queryKey: qk.runner,
    queryFn: () => apiRequest<EmployeeRunnerView>('/employee/runner'),
  });
}

export function useRestartRunner(): UseMutationResult<
  { state: RunnerState },
  Error,
  { force?: boolean }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ force }) =>
      apiRequest<{ state: RunnerState }>('/employee/runner/restart', {
        method: 'POST',
        query: force ? { force: 1 } : {},
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.runner });
    },
  });
}

export function useUsage(from?: string, to?: string): UseQueryResult<EmployeeUsageResponse, Error> {
  return useQuery({
    queryKey: qk.usage(from, to),
    queryFn: () =>
      apiRequest<EmployeeUsageResponse>('/employee/usage', {
        query: { from: from ?? undefined, to: to ?? undefined },
      }),
  });
}

/* ── threads & turns (§4.3) ───────────────────────────────────────────────── */

/** Cursor pagination per CONTRACTS §4.3 (`nextCursor`). */
export function useThreads(archived: boolean): UseInfiniteQueryResult<
  InfiniteData<ThreadListResponse, string | null>,
  Error
> {
  return useInfiniteQuery({
    queryKey: qk.threads(archived),
    queryFn: ({ pageParam }) =>
      apiRequest<ThreadListResponse>('/threads', {
        query: { limit: 30, archived: archived ? 1 : 0, cursor: pageParam ?? undefined },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}

export function useCreateThread(): UseMutationResult<
  { id: string; title: string; createdAt: number },
  Error,
  { title?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiRequest<{ id: string; title: string; createdAt: number }>('/threads', {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['threads'] });
    },
  });
}

export function useThread(
  id: string | undefined,
): UseQueryResult<ThreadDetailResponse, Error> {
  return useQuery({
    queryKey: qk.thread(id ?? ''),
    queryFn: () => apiRequest<ThreadDetailResponse>(`/threads/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

export interface OlderMessagesPage {
  items: MessageView[];
  toolCalls: ToolCallView[];
  hasMore: boolean;
}

export async function fetchOlderMessages(
  threadId: string,
  before: number,
): Promise<OlderMessagesPage> {
  return await apiRequest<OlderMessagesPage>(`/threads/${threadId}/messages`, {
    query: { before, limit: 50 },
  });
}

export function usePatchThread(): UseMutationResult<
  { thread: ThreadView },
  Error,
  { id: string; title?: string; archived?: boolean }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) =>
      apiRequest<{ thread: ThreadView }>(`/threads/${id}`, { method: 'PATCH', body }),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ['threads'] });
      void qc.invalidateQueries({ queryKey: qk.thread(variables.id) });
    },
  });
}

export function useArchiveThread(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiRequest<void>(`/threads/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['threads'] });
    },
  });
}

export function useSendTurn(): UseMutationResult<
  CreateTurnResponse,
  Error,
  { threadId: string; text: string }
> {
  return useMutation({
    mutationFn: ({ threadId, text }) =>
      apiRequest<CreateTurnResponse>(`/threads/${threadId}/turns`, {
        method: 'POST',
        body: { text },
      }),
  });
}

export function useInterruptTurn(): UseMutationResult<{ turnId: string }, Error, string> {
  return useMutation({
    mutationFn: (threadId) =>
      apiRequest<{ turnId: string }>(`/threads/${threadId}/interrupt`, { method: 'POST' }),
  });
}

/* ── approvals (§4.4) ─────────────────────────────────────────────────────── */

export function usePendingApprovals(enabled = true): UseQueryResult<ApprovalView[], Error> {
  return useQuery({
    queryKey: qk.approvals,
    queryFn: async () => {
      const res = await apiRequest<{ items: ApprovalView[] }>('/approvals', {
        query: { status: 'pending' },
      });
      return res.items;
    },
    enabled,
  });
}

export interface ResolveApprovalInput {
  readonly id: string;
  readonly decision: 'allow' | 'deny';
  readonly scope: 'once' | 'always';
  readonly note?: string;
}

export function useResolveApproval(): UseMutationResult<
  ResolveApprovalResponse,
  Error,
  ResolveApprovalInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision, scope, note }) =>
      apiRequest<ResolveApprovalResponse>(`/approvals/${id}/resolve`, {
        method: 'POST',
        body: { decision, scope, ...(note ? { note } : {}) },
      }),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: qk.approvals });
      if (variables.scope === 'always') {
        void qc.invalidateQueries({ queryKey: qk.toolGrants });
      }
    },
  });
}

/* ── memory (§4.5) ────────────────────────────────────────────────────────── */

export function useMemoryTree(path: string): UseQueryResult<MemoryTreeResponse, Error> {
  return useQuery({
    queryKey: qk.memoryTree(path),
    queryFn: () => apiRequest<MemoryTreeResponse>('/memory/tree', { query: { path } }),
  });
}

export function useMemoryFile(path: string | null): UseQueryResult<FileContentResponse, Error> {
  return useQuery({
    queryKey: qk.memoryFile(path ?? ''),
    queryFn: () => apiRequest<FileContentResponse>('/memory/file', { query: { path: path ?? '' } }),
    enabled: Boolean(path),
  });
}

export function useMemoryIndex(): UseQueryResult<MemoryIndexResponse, Error> {
  return useQuery({
    queryKey: qk.memoryIndex,
    queryFn: () => apiRequest<MemoryIndexResponse>('/memory/index'),
  });
}

export function useClaudeMd(): UseQueryResult<ClaudeMdResponse, Error> {
  return useQuery({
    queryKey: qk.claudeMd,
    queryFn: () => apiRequest<ClaudeMdResponse>('/memory/claude-md'),
  });
}

export function usePutClaudeMd(): UseMutationResult<
  { sha256: string; revisionId: string },
  Error,
  { content: string; expectedSha256?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiRequest<{ sha256: string; revisionId: string }>('/memory/claude-md', {
        method: 'PUT',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.claudeMd });
      void qc.invalidateQueries({ queryKey: qk.claudeMdRevisions });
    },
  });
}

export function useClaudeMdRevisions(enabled: boolean): UseQueryResult<ClaudeMdRevision[], Error> {
  return useQuery({
    queryKey: qk.claudeMdRevisions,
    queryFn: async () => {
      const res = await apiRequest<{ items: ClaudeMdRevision[] }>('/memory/claude-md/revisions');
      return res.items;
    },
    enabled,
  });
}

export function useRestoreClaudeMd(): UseMutationResult<
  { sha256: string; revisionId: string },
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (revisionId) =>
      apiRequest<{ sha256: string; revisionId: string }>(
        `/memory/claude-md/revisions/${revisionId}/restore`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.claudeMd });
      void qc.invalidateQueries({ queryKey: qk.claudeMdRevisions });
    },
  });
}

export function useTaskLog(tail: number): UseQueryResult<
  { content: string; truncated: boolean },
  Error
> {
  return useQuery({
    queryKey: qk.taskLog(tail),
    queryFn: () =>
      apiRequest<{ content: string; truncated: boolean }>('/memory/task-log', { query: { tail } }),
  });
}

export function memoryDownloadUrl(path: string): string {
  return downloadUrl('/memory/download', { path });
}

/* ── spaces (§4.6) ────────────────────────────────────────────────────────── */

export function useSpaces(): UseQueryResult<SpaceListItem[], Error> {
  return useQuery({
    queryKey: qk.spaces,
    queryFn: async () => {
      const res = await apiRequest<{ items: SpaceListItem[] }>('/spaces');
      return res.items;
    },
  });
}

export function useSpaceTree(slug: string, path: string): UseQueryResult<MemoryTreeResponse, Error> {
  return useQuery({
    queryKey: qk.spaceTree(slug, path),
    queryFn: () => apiRequest<MemoryTreeResponse>(`/spaces/${slug}/tree`, { query: { path } }),
    enabled: Boolean(slug),
  });
}

export function useSpaceFile(
  slug: string,
  path: string | null,
): UseQueryResult<FileContentResponse, Error> {
  return useQuery({
    queryKey: qk.spaceFile(slug, path ?? ''),
    queryFn: () =>
      apiRequest<FileContentResponse>(`/spaces/${slug}/file`, { query: { path: path ?? '' } }),
    enabled: Boolean(slug) && Boolean(path),
  });
}

export function useSpaceHistory(
  slug: string,
  path: string,
  enabled: boolean,
): UseQueryResult<SpaceHistoryItem[], Error> {
  return useQuery({
    queryKey: qk.spaceHistory(slug, path),
    queryFn: async () => {
      const res = await apiRequest<{ items: SpaceHistoryItem[] }>(`/spaces/${slug}/history`, {
        query: { path: path || undefined, limit: 30 },
      });
      return res.items;
    },
    enabled: enabled && Boolean(slug),
  });
}

export function useSpaceBlob(
  slug: string,
  sha: string | null,
  path: string | null,
): UseQueryResult<{ content: string; truncated: boolean }, Error> {
  return useQuery({
    queryKey: qk.spaceBlob(slug, sha ?? '', path ?? ''),
    queryFn: () =>
      apiRequest<{ content: string; truncated: boolean }>(`/spaces/${slug}/blob`, {
        query: { sha: sha ?? '', path: path ?? '' },
      }),
    enabled: Boolean(slug) && Boolean(sha) && Boolean(path),
  });
}

export function spaceDownloadUrl(slug: string, path: string): string {
  return downloadUrl(`/spaces/${slug}/download`, { path });
}
